import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { sendBulkEmail } from "@/lib/emailService";
import { Mail, User, MessageSquare, Send, CheckCircle } from "lucide-react";

// Email service configuration
const EMAIL_TEMPLATE_ID = "template_y47bxkc";
const EMAIL_SERVICE_ID = "service_3edbwuj";
const EMAIL_PUBLIC_KEY = "pYRLv9sYDpYiLevTX";
const CONTACT_EMAIL_ADDRESS = "info@cittasya.com";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageWordCount, setMessageWordCount] = useState(0);
  const { toast } = useToast();

  const MAX_WORDS = 500; // Maximum words allowed in message

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // Handle message word count validation
    if (name === "message") {
      const words = value
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      if (words.length > MAX_WORDS) {
        toast({
          variant: "destructive",
          title: "Message too long",
          description: `Please keep your message under ${MAX_WORDS} words. Current: ${words.length} words.`,
        });
        return; // Don't update if exceeds word limit
      }
      setMessageWordCount(words.length);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Store submission in Firestore
      await addDoc(collection(db, "contact_web"), {
        fullName: formData.fullName,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        page: window.location.pathname,
      });

      // Create email content
      const emailSubject = `Contact Form: ${
        formData.subject || "General Inquiry"
      }`;
      const emailContent = `
        <h2>New Contact Form Submission</h2>
        <p><strong>From:</strong> ${formData.fullName}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Subject:</strong> ${
          formData.subject || "General Inquiry"
        }</p>
        <p><strong>Message:</strong></p>
        <div style="background: #f5f5f5; padding: 15px; border-radius: 5px; margin: 10px 0;">
          ${formData.message.replace(/\n/g, "<br>")}
        </div>
        <p><strong>Submitted at:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Page:</strong> ${window.location.pathname}</p>
      `;

      // Send email notification
      const emailResults = await sendBulkEmail(
        EMAIL_TEMPLATE_ID,
        EMAIL_SERVICE_ID,
        EMAIL_PUBLIC_KEY,
        emailSubject,
        emailContent,
        [CONTACT_EMAIL_ADDRESS],
        "Contact Form Submission"
      );

      console.log("📧 Email Results:", emailResults);

      setIsSubmitting(false);
      toast({
        title: "Message Sent Successfully! 🙏",
        description:
          "Thank you for reaching out. We'll get back to you within 48 hours.",
      });
      setFormData({
        fullName: "",
        email: "",
        subject: "",
        message: "",
      });
      setMessageWordCount(0);
    } catch (error) {
      setIsSubmitting(false);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "Could not send your message. Please try again later.",
      });
      console.error("Contact form error:", error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="relative bg-cloud-white rounded-3xl p-8 lg:p-12 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] border border-misty-sage/20 backdrop-blur-sm overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-soft-saffron/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-forest-green/10 rounded-full blur-2xl"></div>
      {/* Header */}
      <motion.div variants={itemVariants} className="text-center mb-10">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-forest-green flex items-center justify-center shadow-lg">
            <MessageSquare className="w-6 h-6 text-cloud-white" />
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-forest-green">
            Send Us a Message
          </h2>
        </div>
        <p className="text-forest-green/70 text-lg max-w-2xl mx-auto">
          We'd love to hear from you. Share your thoughts and we'll get back to
          you within 48 hours.
        </p>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
        {/* Name and Email Row */}
        <motion.div
          variants={itemVariants}
          className="grid md:grid-cols-2 gap-6"
        >
          <div className="group">
            <Label
              htmlFor="fullName"
              className="flex items-center gap-2 text-forest-green font-semibold mb-3"
            >
              <User className="w-4 h-4" />
              Full Name *
            </Label>
            <div className="relative">
              <Input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="h-14 pl-12 pr-4 border-2 border-misty-sage/30 focus:border-soft-saffron focus:ring-4 focus:ring-soft-saffron/20 rounded-xl bg-cloud-white/80 backdrop-blur-sm text-forest-green placeholder:text-forest-green/50 transition-all duration-300 group-hover:border-soft-saffron/50"
                placeholder="Enter your full name"
              />
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-forest-green/40 group-focus-within:text-soft-saffron transition-colors" />
            </div>
          </div>

          <div className="group">
            <Label
              htmlFor="email"
              className="flex items-center gap-2 text-forest-green font-semibold mb-3"
            >
              <Mail className="w-4 h-4" />
              Email Address *
            </Label>
            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="h-14 pl-12 pr-4 border-2 border-misty-sage/30 focus:border-soft-saffron focus:ring-4 focus:ring-soft-saffron/20 rounded-xl bg-cloud-white/80 backdrop-blur-sm text-forest-green placeholder:text-forest-green/50 transition-all duration-300 group-hover:border-soft-saffron/50"
                placeholder="your.email@example.com"
              />
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-forest-green/40 group-focus-within:text-soft-saffron transition-colors" />
            </div>
          </div>
        </motion.div>

        {/* Subject */}
        <motion.div variants={itemVariants} className="group">
          <Label
            htmlFor="subject"
            className="flex items-center gap-2 text-forest-green font-semibold mb-3"
          >
            <MessageSquare className="w-4 h-4" />
            Subject
          </Label>
          <div className="relative">
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              className="w-full h-14 pl-12 pr-10 text-forest-green bg-cloud-white/80 backdrop-blur-sm border-2 border-misty-sage/30 focus:border-soft-saffron focus:ring-4 focus:ring-soft-saffron/20 rounded-xl transition-all duration-300 group-hover:border-soft-saffron/50 appearance-none cursor-pointer"
            >
              <option value="">Select a topic</option>
              <option value="inquiry">General Inquiry</option>
              <option value="feedback">Feedback</option>
              <option value="technical">Technical Support</option>
              <option value="collaboration">Collaboration</option>
              <option value="blog">Blog</option>
              <option value="other">Other</option>
            </select>
            <MessageSquare className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-forest-green/40 group-focus-within:text-soft-saffron transition-colors pointer-events-none" />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg
                className="w-5 h-5 text-forest-green/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </motion.div>

        {/* Message */}
        <motion.div variants={itemVariants} className="group">
          <Label
            htmlFor="message"
            className="flex items-center gap-2 text-forest-green font-semibold mb-3"
          >
            <MessageSquare className="w-4 h-4" />
            Message *
          </Label>
          <div className="relative">
            <Textarea
              id="message"
              name="message"
              required
              rows={6}
              value={formData.message}
              onChange={handleInputChange}
              className="w-full pl-12 pr-16 pt-4 pb-8 border-2 border-misty-sage/50 focus:border-soft-saffron focus:ring-4 focus:ring-soft-saffron/20 rounded-xl bg-cloud-white/80 backdrop-blur-sm text-forest-green placeholder:text-forest-green/80 resize-none transition-all duration-300 group-hover:border-soft-saffron/50"
              placeholder="Share your thoughts, questions, or how we can help you on your wellness journey. We're here to listen and support you every step of the way..."
            />
            <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-forest-green/40 group-focus-within:text-soft-saffron transition-colors" />
            <div className="absolute bottom-2 right-3 pointer-events-none">
              <p
                className={`text-xs font-medium ${
                  messageWordCount > MAX_WORDS * 0.9
                    ? "text-red-500"
                    : messageWordCount > MAX_WORDS * 0.7
                    ? "text-yellow-600"
                    : "text-forest-green/60"
                }`}
              >
                {messageWordCount}/{MAX_WORDS}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div variants={itemVariants}>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="group relative w-full h-16 bg-forest-green hover:bg-copper-brown text-cloud-white text-lg font-semibold rounded-2xl transition-all duration-500 transform hover:scale-[1.02] hover:shadow-2xl disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
          >
            {/* Button background animation */}
            <div className="absolute inset-0 bg-soft-saffron/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative flex items-center justify-center gap-3">
              {isSubmitting ? (
                <>
                  <div className="w-6 h-6 border-2 border-cloud-white/30 border-t-cloud-white rounded-full animate-spin"></div>
                  Sending Message...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  Send Message
                </>
              )}
            </div>
          </Button>
        </motion.div>

        {/* Success indicator */}
        {isSubmitting && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-center gap-2 text-forest-green font-medium"
          >
            <CheckCircle className="w-5 h-5 text-soft-saffron" />
            <span>Preparing your message...</span>
          </motion.div>
        )}
      </form>

      {/* Bottom decorative text */}
      {/* <motion.div
        variants={itemVariants}
        className="text-center mt-8 pt-6 border-t border-misty-sage/20"
      >
        <p className="text-forest-green/60 text-sm">
          🙏 Your privacy is important to us. We'll never share your
          information.
        </p>
      </motion.div> */}
    </motion.div>
  );
};

export default ContactForm;
